import React, { useMemo } from 'react'
import { ShoppingBag, ChevronRight } from 'lucide-react'
import Card from '../Card'
import { useNavigate } from 'react-router-dom'
import DataTable, { Column } from '../DataTable'

interface Order {
  id: string
  orderNumber: string
  customerName: string
  productCount: number
  totalAmount: number | string | { toFixed?: (places: number) => string }
  paymentStatus: string
  orderStatus: string
  createdAt: string
}

interface RecentOrdersTableProps {
  orders: Order[]
  loading?: boolean
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    CONFIRMED: 'bg-blue-100 text-blue-800',
    PROCESSING: 'bg-purple-100 text-purple-800',
    SHIPPED: 'bg-cyan-100 text-cyan-800',
    DELIVERED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-red-100 text-red-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

const getPaymentStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    PAID: 'bg-green-100 text-green-800',
    FAILED: 'bg-red-100 text-red-800',
    REFUNDED: 'bg-gray-100 text-gray-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

export default function RecentOrdersTable({ orders, loading = false }: RecentOrdersTableProps) {
  const navigate = useNavigate()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const columns: Column<Order>[] = useMemo(
    () => [
      {
        key: 'orderNumber',
        header: 'Order',
        render: (value) => `#${value}`,
        sortable: true,
        sortType: 'text',
      },
      {
        key: 'customerName',
        header: 'Customer',
        sortable: true,
        sortType: 'text',
      },
      {
        key: 'productCount',
        header: 'Items',
        sortable: true,
        sortType: 'number',
      },
      {
        key: 'totalAmount',
        header: 'Amount',
        accessor: (row) => Number(row.totalAmount),
        render: (value) => `₹${Number(value).toFixed(2)}`,
        sortable: true,
        sortType: 'price',
      },
      {
        key: 'paymentStatus',
        header: 'Payment',
        render: (value) => (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(value)}`}>
            {value}
          </span>
        ),
        sortable: true,
        sortType: 'status',
      },
      {
        key: 'orderStatus',
        header: 'Status',
        render: (value) => (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(value)}`}>
            {value}
          </span>
        ),
        sortable: true,
        sortType: 'status',
      },
      {
        key: 'createdAt',
        header: 'Date',
        render: (value) => formatDate(value),
        sortable: true,
        sortType: 'date',
      },
    ],
    []
  )

  if (loading) {
    return (
      <Card title="Recent Orders" subtitle="Latest 10 orders">
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-lg"></div>
          ))}
        </div>
      </Card>
    )
  }

  if (!orders || orders.length === 0) {
    return (
      <Card title="Recent Orders" subtitle="Latest 10 orders">
        <div className="py-12 flex flex-col items-center justify-center text-gray-500">
          <ShoppingBag size={40} className="mb-2 opacity-50" />
          <p>No orders yet</p>
        </div>
      </Card>
    )
  }

  return (
    <Card title="Recent Orders" subtitle="Latest 10 orders">
      <DataTable<Order>
        columns={columns}
        data={orders}
        onRowClick={(order) => navigate(`/orders/${order.id}`)}
      />
      <button
        onClick={() => navigate('/orders')}
        className="mt-4 w-full py-2 text-center text-[#6D3A0D] font-medium hover:bg-gray-50 rounded-lg transition-colors"
      >
        View All Orders
      </button>
    </Card>
  )
}
